import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nexus/ui";
import { useBrandManagement } from "../../hooks/usePreflightBrands";
import { ManageBrandsDialog } from "../ManageBrandsDialog/ManageBrandsDialog";
import type { PreflightBrand } from "../../types/preflight.types";

const EYEBROW_LABEL =
  "text-muted-foreground text-[11px] font-semibold tracking-wider uppercase";
const TRIGGER_ID = "preflight-brand";

export interface BrandSelectProps {
  brands: PreflightBrand[];
  /** The selected brand name; null shows the placeholder. */
  value: string | null;
  onChange: (brandName: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  /** The list failed to load; shown instead of an empty list so it is never mistaken for one. */
  isError?: boolean;
  onRetry?: () => void;
}

/**
 * The report's brand, picked from the tenant's controlled list (AD-3347). A caller who
 * can curate the list gets a Manage action beside the label; one who cannot add, facing
 * an empty list, is told who can fix it instead of meeting a dead, unexplained dropdown.
 */
export function BrandSelect({
  brands,
  value,
  onChange,
  disabled = false,
  isLoading = false,
  isError = false,
  onRetry,
}: BrandSelectProps) {
  const { t } = useTranslation();
  const { canAdd, canOpenManage } = useBrandManagement(brands);
  const [isManageOpen, setIsManageOpen] = useState(false);

  const hasLoadFailed = isError && brands.length === 0;
  const isEmpty = !isLoading && !hasLoadFailed && brands.length === 0;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={TRIGGER_ID} className={EYEBROW_LABEL}>
          {t("preflight.inputCard.brand", "Brand")}{" "}
          <span className="text-error font-normal" aria-hidden="true">
            *
          </span>
        </Label>
        {canOpenManage ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => setIsManageOpen(true)}
          >
            {t("preflight.brands.manage", "Manage brands")}
          </Button>
        ) : null}
      </div>

      <Select
        value={value ?? ""}
        onValueChange={onChange}
        disabled={disabled || isLoading || isEmpty || hasLoadFailed}
      >
        <SelectTrigger id={TRIGGER_ID} className="w-full">
          <SelectValue
            placeholder={
              isLoading
                ? t("preflight.brands.loading", "Loading brands…")
                : hasLoadFailed
                  ? t("preflight.brands.loadFailedPlaceholder", "Brands unavailable")
                  : isEmpty
                  ? t("preflight.brands.selectEmpty", "No brands yet")
                  : t("preflight.brands.selectPlaceholder", "Select a brand")
            }
          />
        </SelectTrigger>
        <SelectContent>
          {brands.map((brand) => (
            <SelectItem key={brand.id} value={brand.name}>
              {brand.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasLoadFailed ? (
        <p role="alert" className="text-error flex items-center gap-1 text-xs">
          {t("preflight.brands.loadFailed", "Couldn't load brands.")}
          {onRetry ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={onRetry}
            >
              {t("preflight.brands.retry", "Try again")}
            </Button>
          ) : null}
        </p>
      ) : null}

      {isEmpty ? (
        <p className="text-muted-foreground text-xs">
          {canAdd
            ? t("preflight.brands.emptyHintManage", "Add the brands your creatives feature with Manage brands.")
            : t("preflight.brands.emptyHintAskAdmin", "Ask an admin to add your brands.")}
        </p>
      ) : null}

      {isManageOpen ? (
        <ManageBrandsDialog open={isManageOpen} onOpenChange={setIsManageOpen} brands={brands} />
      ) : null}
    </div>
  );
}
